import { Component, computed, ElementRef, inject, signal, ViewChild } from '@angular/core';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { MatNavList } from '@angular/material/list';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { LoadingSpinnerComponent } from "../../../../shared/components/loading-spinner/loading-spinner.component";
import { AddTenantDialogComponent } from '../../../../features/tenant-management/dialogs/add-tenant-dialog/add-tenant-dialog.component';
import { TenantService } from '../../../../features/tenant-management/services/tenant.service';
import { TenantContextService } from '../../../../features/tenant-management/services/tenant-context.service';
import { Tenant } from '../../../../features/tenant-management/models/tenant.model';
import { GetTenantRequest } from '../../../../features/tenant-management/models/get-tenant-request';

@Component({
  selector: 'app-tenant-sidebar',
  imports: [
    MatInputModule,
    MatFormFieldModule,
    MatButtonModule,
    MatTooltipModule,
    MatNavList,
    MatIconModule,
    MatCardModule,
    LoadingSpinnerComponent
],
  templateUrl: './tenant-sidebar.component.html',
  styleUrl: './tenant-sidebar.component.css'
})
export class TenantSidebarComponent {
  @ViewChild('input') searchInput!: ElementRef;

  tenantService = inject(TenantService);
  tenantContext = inject(TenantContextService);

  searchFilter = signal('');
  statusFilter = signal<'all' | 'enabled' | 'disabled'>('all');

  constructor(private router: Router, private dialog: MatDialog) {}

  filteredTenants = computed(() => {
    const search = this.searchFilter().toLowerCase();
    const status = this.statusFilter();
    const ID_INCLUDES_THRESHOLD = 4;

    let searchList = this.tenantContext.tenantList().filter(tenant => {
      const matchesSearch = tenant.tenantName.toLowerCase().includes(search) ||
        (tenant.tenantAlias !== null && tenant.tenantAlias.toLowerCase().includes(search)) ||
        (search.length >= ID_INCLUDES_THRESHOLD ? tenant.tenantId.includes(search) : tenant.tenantId.toLowerCase().startsWith(search));
      
      const matchesStatus = status === 'all' || 
        (status === 'enabled' && tenant.isEnabled) ||
        (status === 'disabled' && !tenant.isEnabled);

      return matchesSearch && matchesStatus;
    });
    
    searchList.sort((a, b) => a.tenantName.localeCompare(b.tenantName) || (a.tenantAlias ?? '').localeCompare(b.tenantAlias ?? ''));

    return searchList;
  });

  tenantCounts = computed(() => ({
    total: this.tenantContext.tenantList().length,
    enabled: this.tenantContext.tenantList().filter(t => t.isEnabled).length,
    disabled: this.tenantContext.tenantList().filter(t => !t.isEnabled).length
  }));

  setStatusFilter(status: 'all' | 'enabled' | 'disabled'): void {
    this.statusFilter.set(status);
  }

  onTenantSelected(tenant: Tenant): void {
    this.router.navigate(['/tenant-management', tenant.tenantId]);
  }

  openAddTenantDialog(): void {
    const dialogRef = this.dialog.open(AddTenantDialogComponent, {
      width: '560px',
      disableClose: true,
    });
  
    dialogRef.beforeClosed().subscribe((result: string) => {
      if (result) {
        const request: GetTenantRequest = { tenantId: result}
        this.tenantContext.isLoading.set(true);

        this.tenantService.getTenant(request).subscribe({
          next: (tenant) => {
            if (tenant !== null) {
              this.tenantContext.tenantList.set([...this.tenantContext.tenantList(), tenant]);
              this.router.navigate(['/tenant-management', tenant.tenantId]);
              this.tenantContext.isLoading.set(false);
            }
          },
          error: (error) => {
            console.log(error);
            this.tenantContext.isLoading.set(false);
          }
        });
      }
    });
  }

  onSearchChanged(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchFilter.set(filterValue);
  }

  removeSearch(): void {
    this.searchInput.nativeElement.value = '';
    this.searchFilter.set('');
  }
}
